import { FuelType, Vehicle, VehicleLocation, VehicleStats } from "../../../models";

// mpg is a NUMERIC column; shopspring/decimal can serialize as a number or a
// numeric string, so accept both.
const num = (v: any): number | undefined =>
  typeof v === "number"
    ? v
    : typeof v === "string" && v.trim() !== "" && !isNaN(Number(v))
    ? Number(v)
    : undefined;
const str = (v: any): string | undefined => (typeof v === "string" ? v : undefined);
const bool = (v: any): boolean | undefined => (typeof v === "boolean" ? v : undefined);

// Exported for reuse by the drivers normalizer — the driver `location` payload
// (from GET /drivers?with_location=true) is the same shape as a vehicle's.
export const getVehicleLocation = (raw: any): VehicleLocation | undefined => {
  if (!raw) return undefined;
  return {
    available: !!raw.available,
    reason: str(raw.reason),
    latitude: num(raw.latitude),
    longitude: num(raw.longitude),
    speedMph: num(raw.speed_mph),
    headingDegrees: num(raw.heading_degrees),
    address: str(raw.address),
    gpsTime: str(raw.gps_time),
    stale: bool(raw.stale),
    fetchedAt: str(raw.fetched_at),
  };
};

const getStats = (raw: any): VehicleStats | undefined => {
  if (!raw) return undefined;
  const faults = raw.faults
    ? {
        checkEngineLight: !!raw.faults.check_engine_light,
        codes: Array.isArray(raw.faults.codes)
          ? raw.faults.codes.map((c: any) => ({ code: c.code, description: c.description }))
          : [],
      }
    : undefined;
  return {
    available: !!raw.available,
    reason: str(raw.reason),
    fuelPercent: num(raw.fuel_percent),
    odometerMiles: num(raw.odometer_miles),
    engineState: str(raw.engine_state) as VehicleStats["engineState"] | undefined,
    faults,
    stale: bool(raw.stale),
    fetchedAt: str(raw.fetched_at),
  };
};

export const getVehicle = (data: any): Vehicle => {
  const a = data.attributes;
  return {
    id: data.id,
    label: a.label,
    make: str(a.make),
    model: str(a.model),
    modelYear: num(a.model_year),
    licensePlate: str(a.license_plate),
    vin: str(a.vin),
    samsaraVehicleId: str(a.samsara_vehicle_id),
    fuelType: (str(a.fuel_type) as FuelType | undefined) ?? "diesel",
    mpg: num(a.mpg),
    location: getVehicleLocation(a.location),
    stats: getStats(a.stats),
  };
};

export const getVehicles = (data: any): Vehicle[] => {
  return Array.isArray(data) ? data.map(getVehicle) : [];
};
